import React, { useCallback, useEffect } from 'react';
import Select, { components } from 'react-select';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const MultiValue = (props) => {
  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const innerProps = { ...props.innerProps, onMouseDown };
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: props.data.value,
    });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div style={style} ref={setNodeRef} {...attributes} {...listeners}>
      <components.MultiValue {...props} innerProps={innerProps} />
    </div>
  );
};

const MultiValueRemove = (props) => {
  return (
    <components.MultiValueRemove
      {...props}
      innerProps={{
        onPointerDown: (e) => e.stopPropagation(),
        ...props.innerProps,
      }}
    />
  );
};

// custome input component to disable autocomplete
const CustomInput = ({ ...rest }) => (
  <components.Input {...rest} autoComplete="new-password" />
);

const optionsFromFieldValue = (fieldValue, options) =>
  fieldValue
    ? fieldValue
        .map((v) => options.find((c) => c.value === v))
        // drop values with no matching option (e.g. the underlying object
        // was deleted) so we never render an undefined selection
        .filter(Boolean)
    : [];

const SortableSelect = (props) => {
  const [selected, setSelected] = React.useState(
    optionsFromFieldValue(props.fieldValue, props.options),
  );

  // selected is otherwise only ever updated by this component's own
  // handlers below, so an external change to fieldValue (a form
  // reset/revert, publish, etc.) would never reach the rendered select
  // without this.
  useEffect(() => {
    setSelected(optionsFromFieldValue(props.fieldValue, props.options));
  }, [props.fieldValue, props.options]);

  const fieldOnChange = (selectedOptions) => {
    // set the local state
    setSelected([...selectedOptions]);
    // call the onChange function from the react hook form field
    props.onChange([...selectedOptions]);
  };

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!active || !over) return;
      setSelected((items) => {
        const oldIndex = items.findIndex((item) => item.value === active.id);
        const newIndex = items.findIndex((item) => item.value === over.id);
        const a = arrayMove(items, oldIndex, newIndex);
        // call the onChange function from the react hook form field
        props.onChange([...a]);
        return a;
      });
      return;
    },
    [setSelected],
  );

  return (
    <DndContext
      modifiers={[restrictToParentElement]}
      onDragEnd={onDragEnd}
      collisionDetection={closestCenter}
    >
      <SortableContext
        items={selected.map((o) => o.value)}
        strategy={horizontalListSortingStrategy}
      >
        <Select
          distance={4}
          isMulti
          value={selected}
          components={{
            MultiValue,
            MultiValueRemove,
            Input: CustomInput,
          }}
          closeMenuOnSelect={false}
          autoComplete="off"
          {...props}
          // make sure we override the default onChange function
          onChange={fieldOnChange}
        />
      </SortableContext>
    </DndContext>
  );
};

export default SortableSelect;
